// ─────────────────────────────────────────────────────────────────────────────
// Mock data — Education Quality dashboard (health + attention + voice)
// ─────────────────────────────────────────────────────────────────────────────

import type { MetricItem } from "@/components/key-metrics"

export type QualityPriority = "high" | "medium" | "low"

export type ExperienceTrendMetric =
  | "overall"
  | "course"
  | "faculty"
  | "clinical"
  | "preceptor"

export interface QualityScopeFilters {
  termLabel: string
  programLabel: string
  campusLabel: string
}

export interface QualityHealthSummary {
  overallExperience: number
  overallDelta: number
  responseRate: number
  responseDelta: number
  learnerCount: number
  evaluationCount: number
  areasNeedingAttention: number
  actionsClosedPercent: number
  clinicalExperience: number
  facultyExperience: number
}

export interface QualityAttentionItem {
  id: string
  area: string
  issue: string
  signal: string
  priority: QualityPriority
  whyFlagged: {
    headline: string
    bullets: string[]
  }
}

export interface QualityThemeChip {
  id: string
  label: string
  count: number
  sentiment: "up" | "down" | "neutral"
  detail?: {
    headline: string
    affected: string[]
  }
}

export interface QualityVoiceItem {
  label: string
  direction: "up" | "down"
}

export interface QualityProgramRow {
  id: string
  name: string
  overall: number
  clinical: number
  faculty: number
  response: number
  trend: "up" | "down" | "neutral"
}

export interface QualityClinicalSite {
  id: string
  name: string
  score: number
  trend: "up" | "down" | "neutral"
  watch?: boolean
}

export interface QualityClinicalMatrixRow {
  site: string
  siteScore: number
  preceptor: number
  exposure: number
  supervision: number
}

export interface QualityParticipationBucket {
  label: string
  count: number
  alert?: boolean
}

export interface QualityLowResponseEval {
  name: string
  rate: number
  closesLabel: string
}

export interface QualityLoadWeek {
  label: string
  value: number
  alert?: boolean
}

export interface QualityImprovementItem {
  id: string
  source: string
  learnerSaid: string
  status: "done" | "in_progress"
  outcome: string
}

export interface QualityAccreditationItem {
  label: string
}

export const QUALITY_SCOPE: QualityScopeFilters = {
  termLabel: "Fall 2025",
  programLabel: "All programs",
  campusLabel: "All campuses",
}

export function evaluationQualityHealthMetrics(h: QualityHealthSummary): MetricItem[] {
  const overallDeltaPrefix = h.overallDelta >= 0 ? "+" : ""

  return [
    {
      id: "overall-experience",
      label: "Overall learner experience",
      value: `${h.overallExperience.toFixed(1)} / 5`,
      delta: `${overallDeltaPrefix}${h.overallDelta.toFixed(1)}`,
      trend: h.overallDelta >= 0 ? "up" : "down",
      description: `${h.responseRate}% response · ${h.learnerCount.toLocaleString()} learners · ${h.evaluationCount} evaluations · ${h.actionsClosedPercent}% actions closed`,
      metricVariant: "hero",
    },
    {
      id: "clinical-experience",
      label: "Clinical experience",
      value: h.clinicalExperience.toFixed(1),
      delta: "",
      trend: "neutral",
    },
    {
      id: "faculty-experience",
      label: "Faculty experience",
      value: h.facultyExperience.toFixed(1),
      delta: "",
      trend: "neutral",
    },
    {
      id: "need-attention",
      label: "Need attention",
      value: h.areasNeedingAttention,
      delta: "",
      trend: "neutral",
      alert: h.areasNeedingAttention > 0 ? "warning" : undefined,
    },
  ]
}

export const QUALITY_HEALTH: QualityHealthSummary = {
  overallExperience: 4.3,
  overallDelta: 0.2,
  responseRate: 78,
  responseDelta: 6,
  learnerCount: 1842,
  evaluationCount: 126,
  areasNeedingAttention: 12,
  actionsClosedPercent: 84,
  clinicalExperience: 4.1,
  facultyExperience: 4.5,
}

export const QUALITY_ATTENTION_ITEMS: QualityAttentionItem[] = [
  {
    id: "surgery-rotation",
    area: "Surgery rotation",
    issue: "Scheduling",
    signal: "↓ 18%",
    priority: "high",
    whyFlagged: {
      headline: "Scheduling sentiment declined sharply this term.",
      bullets: [
        "Rating dropped from 4.1 to 3.4",
        "27 negative comments in the last 14 days",
        "\"Scheduling\" mentioned 19 times",
        "Trend persists across 3 cohorts",
      ],
    },
  },
  {
    id: "mercy-site",
    area: "Mercy clinical site",
    issue: "Site resources",
    signal: "3.2 / 5",
    priority: "high",
    whyFlagged: {
      headline: "Site resources scored below the 3.5 program threshold.",
      bullets: [
        "Learners cite limited workspace and equipment",
        "14 comments reference safety or resource gaps",
        "Preceptor quality remains strong at 4.4",
      ],
    },
  },
  {
    id: "pharm-ii",
    area: "Pharmacology II",
    issue: "Workload",
    signal: "42 comments",
    priority: "medium",
    whyFlagged: {
      headline: "Workload theme spiked after midterm.",
      bullets: [
        "Comment volume up 2.1× vs prior term",
        "Assessment timing overlaps with clinical block",
      ],
    },
  },
  {
    id: "dr-kumar",
    area: "Dr. Kumar",
    issue: "Feedback timeliness",
    signal: "↓ 9%",
    priority: "medium",
    whyFlagged: {
      headline: "Feedback timeliness dropped below department average.",
      bullets: [
        "Median return time increased from 4 to 7 days",
        "Most flags from PA Year 1 cohort",
      ],
    },
  },
]

export const QUALITY_VOICE_IMPROVING: QualityVoiceItem[] = [
  { label: "Clinical exposure", direction: "up" },
  { label: "Faculty accessibility", direction: "up" },
  { label: "Simulation quality", direction: "up" },
]

export const QUALITY_VOICE_ATTENTION: QualityVoiceItem[] = [
  { label: "Rotation scheduling", direction: "down" },
  { label: "Workload balance", direction: "down" },
  { label: "Clinical site resources", direction: "down" },
]

export const QUALITY_THEMES: QualityThemeChip[] = [
  {
    id: "clinical-exposure",
    label: "Clinical exposure",
    count: 126,
    sentiment: "up",
  },
  {
    id: "scheduling",
    label: "Scheduling",
    count: 83,
    sentiment: "down",
    detail: {
      headline: "Scheduling sentiment declined 14%",
      affected: ["Surgery", "Pediatrics", "Community placements"],
    },
  },
  {
    id: "faculty-feedback",
    label: "Faculty feedback",
    count: 64,
    sentiment: "neutral",
  },
  {
    id: "assessment",
    label: "Assessment",
    count: 51,
    sentiment: "neutral",
  },
  {
    id: "site-resources",
    label: "Site resources",
    count: 39,
    sentiment: "down",
  },
]

export const QUALITY_TREND_SERIES: Record<
  ExperienceTrendMetric,
  { label: string; points: { term: string; value: number }[] }
> = {
  overall: {
    label: "Overall",
    points: [
      { term: "2024", value: 4.0 },
      { term: "2025", value: 4.2 },
      { term: "S26", value: 4.4 },
      { term: "F25", value: 4.3 },
    ],
  },
  course: {
    label: "Course",
    points: [
      { term: "2024", value: 3.9 },
      { term: "2025", value: 4.1 },
      { term: "S26", value: 4.3 },
      { term: "F25", value: 4.2 },
    ],
  },
  faculty: {
    label: "Faculty",
    points: [
      { term: "2024", value: 4.2 },
      { term: "2025", value: 4.4 },
      { term: "S26", value: 4.5 },
      { term: "F25", value: 4.5 },
    ],
  },
  clinical: {
    label: "Clinical",
    points: [
      { term: "2024", value: 3.8 },
      { term: "2025", value: 4.0 },
      { term: "S26", value: 4.2 },
      { term: "F25", value: 4.1 },
    ],
  },
  preceptor: {
    label: "Preceptor",
    points: [
      { term: "2024", value: 4.1 },
      { term: "2025", value: 4.3 },
      { term: "S26", value: 4.4 },
      { term: "F25", value: 4.4 },
    ],
  },
}

export const QUALITY_PROGRAMS: QualityProgramRow[] = [
  {
    id: "nursing",
    name: "Nursing",
    overall: 4.5,
    clinical: 4.4,
    faculty: 4.6,
    response: 84,
    trend: "up",
  },
  {
    id: "pt",
    name: "Physical therapy",
    overall: 4.3,
    clinical: 4.2,
    faculty: 4.5,
    response: 79,
    trend: "neutral",
  },
  {
    id: "pa",
    name: "Physician assistant",
    overall: 4.0,
    clinical: 3.7,
    faculty: 4.4,
    response: 72,
    trend: "down",
  },
  {
    id: "ot",
    name: "Occupational therapy",
    overall: 4.4,
    clinical: 4.3,
    faculty: 4.5,
    response: 81,
    trend: "up",
  },
]

export const QUALITY_CLINICAL_TOP: QualityClinicalSite[] = [
  { id: "apollo", name: "Apollo rehabilitation", score: 4.8, trend: "up" },
  { id: "mercy", name: "Mercy health", score: 4.7, trend: "neutral" },
]

export const QUALITY_CLINICAL_WATCH: QualityClinicalSite[] = [
  { id: "city", name: "City hospital", score: 3.5, trend: "down", watch: true },
  { id: "north", name: "North clinic", score: 3.3, trend: "down", watch: true },
]

export const QUALITY_CLINICAL_MATRIX: QualityClinicalMatrixRow[] = [
  { site: "Mercy", siteScore: 4.6, preceptor: 4.7, exposure: 4.5, supervision: 4.6 },
  { site: "City hospital", siteScore: 3.5, preceptor: 4.4, exposure: 3.2, supervision: 3.7 },
  { site: "North clinic", siteScore: 3.3, preceptor: 3.9, exposure: 3.1, supervision: 3.5 },
]

export const QUALITY_PARTICIPATION = {
  rate: 78,
  responded: 1842,
  total: 2361,
  buckets: [
    { label: "On track", count: 32 },
    { label: "Ending soon", count: 8 },
    { label: "Low response", count: 5, alert: true },
  ] satisfies QualityParticipationBucket[],
  lowResponse: [
    { name: "Pediatrics rotation", rate: 41, closesLabel: "closes tomorrow" },
  ] satisfies QualityLowResponseEval[],
}

export const QUALITY_EVAL_LOAD = {
  averageThisWeek: 3.4,
  minutesPerLearner: 8,
  weeks: [
    { label: "W1", value: 2 },
    { label: "W2", value: 3 },
    { label: "W3", value: 7, alert: true },
    { label: "W4", value: 3 },
    { label: "W5", value: 2 },
  ] satisfies QualityLoadWeek[],
  alert: "PA Year 2 students have 8 evaluations this week · 2.4× institution average",
}

export const QUALITY_CLOSING_LOOP = {
  insights: 36,
  actionsCreated: 28,
  inProgress: 24,
  communicated: 18,
  items: [
    {
      id: "sim-lab",
      source: "Simulation lab",
      learnerSaid: "Students asked for more practice time.",
      status: "done",
      outcome: "Added two additional sessions",
    },
    {
      id: "pa-rotation",
      source: "PA clinical rotation",
      learnerSaid: "Learners reported inconsistent schedules.",
      status: "in_progress",
      outcome: "Scheduling workflow being redesigned",
    },
  ] satisfies QualityImprovementItem[],
}

export function evaluationQualityClosingLoopMetrics(
  loop: Pick<
    typeof QUALITY_CLOSING_LOOP,
    "insights" | "actionsCreated" | "inProgress" | "communicated"
  >,
): MetricItem[] {
  return [
    {
      id: "insights",
      label: "Insights identified",
      value: loop.insights,
      delta: "",
      trend: "neutral",
    },
    {
      id: "actions-created",
      label: "Actions created",
      value: loop.actionsCreated,
      delta: "",
      trend: "neutral",
    },
    {
      id: "in-progress",
      label: "In progress",
      value: loop.inProgress,
      delta: "",
      trend: "neutral",
    },
    {
      id: "communicated",
      label: "Communicated",
      value: loop.communicated,
      delta: "",
      trend: "neutral",
    },
  ]
}

export const QUALITY_ACCREDITATION = {
  readyPercent: 92,
  complete: 46,
  total: 50,
  frameworks: ["LCME", "ARC-PA", "CCNE", "ACOTE"],
  needsAttention: [
    { label: "3 courses without documented review" },
    { label: "1 improvement action missing outcome evidence" },
    { label: "Clinical site evaluation below threshold" },
  ] satisfies QualityAccreditationItem[],
}
